import { Role } from '@/features/auth/types';
import { tz } from '@date-fns/tz';
import { type ClassValue, clsx } from 'clsx';
import {
    format,
    formatDistanceToNow,
    isValid,
    parse,
    parseISO,
} from 'date-fns';
import { extendTailwindMerge } from 'tailwind-merge';

import {
    DEFAULT_FILE_TYPE_COLOR,
    FILE_FORMAT_COLORS,
    FILE_TYPE_COLORS,
} from './constants';

/**
 * Parses a date string as UTC regardless of whether it includes a timezone
 * suffix. Backend stores timestamps in UTC but may omit the trailing "Z" or
 * "+00:00". Without explicit handling, `new Date()` treats such strings as
 * local time, shifting the result by the user's UTC offset.
 *
 * Uses `date-fns/parseISO` with `@date-fns/tz` context so the interpretation
 * is always UTC — no fragile regex needed.
 *
 * Returns `null` for unparseable / invalid strings.
 */
export function parseUTCDate(dateString: string): Date | null {
    const date = parseISO(dateString, { in: tz('UTC') });
    if (isValid(date)) {
        // The future-date fallback only applies to datetime strings (a naive
        // backend timestamp misread as UTC). Date-only values carry no time and
        // no ambiguity, so never reinterpret them as local (off-by-one in +UTC).
        const hasTime = dateString.includes('T') || dateString.includes(':');
        if (hasTime && date.getTime() > Date.now() + 60000) {
            const localDate = parseISO(dateString);
            if (isValid(localDate)) {
                return localDate;
            }
        }
        return date;
    }
    return null;
}

// tailwind-merge ships no scale for the theme's font sizes. Unregistered it reads
// `text-caption` as a text colour, so any colour class in the same `cn()` call silently
// deletes the size and the element inherits instead. Keep in step with the `@theme` block
// in assets/styles/globals.css.
const twMerge = extendTailwindMerge({
    extend: {
        classGroups: {
            'font-size': [
                {
                    text: [
                        'base-medium',
                        'base-semibold',
                        'paragraph',
                        'paragraph-ui',
                        'paragraphy-ui-medium',
                        'paragraph-ui-semibold',
                        'leading',
                        'subtle',
                        'subtle-medium',
                        'subtle-semibold',
                        'small',
                        'caption',
                        'caption-medium',
                        'footnote-medium',
                        'micro-medium',
                    ],
                },
            ],
        },
    },
});

export function cn(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
}

export function range(from: number, n: number, step: number = 1) {
    return Array.from({ length: n }, (_, key) => from + key * step);
}

export function formatBytes(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return (
        Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    );
}

/**
 * ISO 8601 date-only (`YYYY-MM-DD`) in UTC. Parses via `parseUTCDate` so
 * timezone-naive backend timestamps stay UTC; returns `—` for missing /
 * invalid. Format-placeholder strings (`yyyy`/`mmm`) pass through unchanged.
 */
export function formatDate(
    dateString: string | Date | null | undefined,
): string {
    if (dateString instanceof Date) {
        if (!Number.isFinite(dateString.getTime())) {
            return '—';
        }
        return format(dateString, 'yyyy-MM-dd', { in: tz('UTC') });
    }

    if (
        !dateString ||
        dateString.includes('yyyy') ||
        dateString.includes('mmm')
    ) {
        return dateString || '—';
    }

    try {
        const date = parseUTCDate(dateString);
        if (!date) return '—';
        return format(date, 'yyyy-MM-dd', { in: tz('UTC') });
    } catch (error) {
        return '—';
    }
}

/**
 * Format a Date as `YYYY-MM-DD` using LOCAL calendar parts.
 *
 * Unlike {@link formatDate}, which converts to UTC, this preserves the calendar day the
 * user actually picked (e.g. from a DatePicker), avoiding an off-by-one for users whose
 * timezone differs from UTC. Use for date-only values with no time/zone meaning.
 */
export function formatLocalDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

/**
 * Parses a `YYYY-MM-DD` date-only string into a `Date` at LOCAL midnight —
 * the inverse of {@link formatLocalDate}.
 *
 * `new Date('YYYY-MM-DD')` parses as UTC midnight per the ECMAScript spec.
 * Formatting that result back out in local time (e.g. handing it to a
 * DatePicker) renders the previous day for any user west of UTC. Use for
 * date-only values with no time/zone meaning — the same class of value
 * formatLocalDate produces.
 */
export function parseLocalDate(dateString: string): Date | undefined {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString);
    if (!match) return undefined;
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day));
}

/**
 * ISO 8601 date+time (`YYYY-MM-DDTHH:mm:ssZ`, second precision) in UTC.
 * Parses via `parseUTCDate`; returns `—` for missing / invalid.
 */
export function formatDateTime(
    dateString: string | Date | null | undefined,
): string {
    if (dateString instanceof Date) {
        if (!Number.isFinite(dateString.getTime())) {
            return '—';
        }
        return format(dateString, "yyyy-MM-dd'T'HH:mm:ss'Z'", {
            in: tz('UTC'),
        });
    }

    if (!dateString) return '—';

    try {
        const date = parseUTCDate(dateString);
        if (!date) return '—';
        return format(date, "yyyy-MM-dd'T'HH:mm:ss'Z'", { in: tz('UTC') });
    } catch (error) {
        return '—';
    }
}

/**
 * Formats a date as relative time (e.g., "2 months ago", "5 days ago").
 */
export function formatRelativeTime(
    dateString: string | Date | null | undefined,
): string {
    if (!dateString) return '—';

    try {
        const date =
            dateString instanceof Date ? dateString : parseUTCDate(dateString);
        if (!date || !isValid(date)) return '—';
        return formatDistanceToNow(date, { addSuffix: true });
    } catch {
        return '—';
    }
}

/**
 * Parses a date string in various formats (DD/MM/YYYY, ISO, etc.)
 */
export function parseFlexibleDate(
    dateString: string | null | undefined,
): Date | null {
    if (!dateString) return null;

    // Try parsing as DD/MM/YYYY format first
    let date = parse(dateString, 'dd/MM/yyyy', new Date());

    // If that fails, try ISO format or other formats
    if (!isValid(date)) {
        date = new Date(dateString);
    }

    return isValid(date) ? date : null;
}

export function isMarkdownContent(content: string): boolean {
    // Check for common Markdown patterns
    const markdownPatterns = [
        /^#+ /m, // Headers
        /\*\*.+\*\*/, // Bold text
        /\*.+\*/, // Italic text
        /^\s*[-*+] /m, // List items
        /^\s*\d+\. /m, // Numbered lists
        /\[.+\]$$.+$$/, // Links
        /^>/m, // Blockquotes
        /^```/m, // Code blocks
        /^## /m, // ## headers specifically
    ];

    return markdownPatterns.some((pattern) => pattern.test(content));
}

const FILE_TYPE_ABBREVIATION_OVERRIDES: Record<string, string> = {
    'Discrepancy Report': 'DIR',
    'Damage Reports': 'DR',
    'Removal/Installation List': 'RIL',
    'Last Done / Next Due': 'LDND',
};

export const generateFileTypeAbbreviation = (label: string): string => {
    if (FILE_TYPE_ABBREVIATION_OVERRIDES[label]) {
        return FILE_TYPE_ABBREVIATION_OVERRIDES[label];
    }

    const words = label.split(' ');
    if (words.length === 1) {
        return label.slice(0, 2).toUpperCase();
    }
    if (words.length === 2) {
        return words
            .map((word) => word[0])
            .join('')
            .toUpperCase();
    }
    return words
        .slice(0, 3)
        .map((word) => word[0])
        .join('')
        .toUpperCase();
};

export const getFileTypeColor = (fileType: string): string => {
    return (
        FILE_TYPE_COLORS[fileType as keyof typeof FILE_TYPE_COLORS] ||
        DEFAULT_FILE_TYPE_COLOR
    );
};

// Keyed by file extension, unlike getFileTypeColor which is keyed by document type
export const getFileFormatBadgeColor = (filename: string): string => {
    const raw = filename.split('.').pop()?.toLowerCase() ?? '';
    const ext = raw === 'jpeg' ? 'jpg' : raw === 'tif' ? 'tiff' : raw;
    return FILE_FORMAT_COLORS[ext] ?? DEFAULT_FILE_TYPE_COLOR;
};

// Empty string for ZIP and unrecognised extensions, which render no badge
export const getFileFormatLabel = (filename: string): string => {
    const raw = filename.split('.').pop()?.toLowerCase() ?? '';
    const LABELLED_FORMATS = new Set([
        'pdf',
        'csv',
        'xlsx',
        'xls',
        'jpg',
        'jpeg',
        'png',
        'tiff',
        'tif',
    ]);
    if (!LABELLED_FORMATS.has(raw)) return '';
    const normalised = raw === 'jpeg' ? 'jpg' : raw === 'tif' ? 'tiff' : raw;
    return normalised.toUpperCase();
};

// Utility function to determine ingestion type based on file configuration
export const determineIngestionType = (
    fileType: string | undefined,
    useAi: boolean,
    allowedTypes: readonly string[],
): string => {
    // If using AI or no specific file type, use docs
    if (useAi || !fileType) {
        return 'docs';
    }

    // If file type is in allowed list, use it directly
    if (allowedTypes.includes(fileType)) {
        return fileType;
    }

    // Default fallback
    return 'docs';
};

export function pluralize(
    count: number,
    singular: string,
    plural?: string,
): string {
    const pluralForm = plural || `${singular}s`;
    return count === 1 ? singular : pluralForm;
}

export function formatCount(
    count: number,
    singular: string,
    plural?: string,
): string {
    return `${count} ${pluralize(count, singular, plural)}`;
}

/**
 * Truncates a string in the middle, preserving the start and end, inserting
 * an ellipsis character. Returns the original string unchanged when its length
 * is at or below maxLength.
 *
 * Example: truncateMiddle('/IATA_Binder/Dangerous_Goods/long_file.pdf', 30)
 *   → '/IATA_Binder/…long_file.pdf'
 */
export function truncateMiddle(text: string, maxLength = 40): string {
    if (text.length <= maxLength) return text;
    const ellipsis = '…';
    // Keep roughly equal head and tail; tail gets the extra char if odd
    const keepTotal = maxLength - 1; // 1 char for ellipsis
    const headLen = Math.floor(keepTotal / 2);
    const tailLen = keepTotal - headLen;
    return (
        text.slice(0, headLen) + ellipsis + text.slice(text.length - tailLen)
    );
}

const ROLE_LABELS: Record<string, string> = {
    [Role.viewer]: 'Viewer',
    [Role.editor]: 'Editor',
    [Role.admin]: 'Admin',
    [Role.endOfLeaseReader]: 'End of Lease Reader',
    [Role.endOfLeaseEditor]: 'End of Lease Editor',
    [Role.endOfLeaseAdmin]: 'End of Lease Admin',
    [Role.maintenanceReader]: 'Maintenance Reader',
    [Role.maintenanceEditor]: 'Maintenance Editor',
    [Role.maintenanceAdmin]: 'Maintenance Admin',
    [Role.maintenanceReviewReader]: 'Maintenance Review Reader',
    [Role.maintenanceReviewEditor]: 'Maintenance Review Editor',
    [Role.maintenanceReviewAdmin]: 'Maintenance Review Admin',
    [Role.asset360Reader]: 'Asset 360 Reader',
    [Role.asset360Editor]: 'Asset 360 Editor',
    [Role.asset360Admin]: 'Asset 360 Admin',
    [Role.serviceBulletinReader]: 'Service Bulletin Reader',
    [Role.serviceBulletinEditor]: 'Service Bulletin Editor',
    [Role.serviceBulletinAdmin]: 'Service Bulletin Admin',
};

export function formatRoleLabel(role: Role): string {
    return ROLE_LABELS[role] ?? role;
}

/**
 * Appends a PDF page anchor (#page=N) to a preview URL.
 * page_start is 0-indexed (from DB); the #page= fragment is 1-indexed.
 * Returns undefined if no URL is provided, or the plain URL if no page is given.
 */
export function getPageAnchoredUrl(
    url: string | null | undefined,
    pageStart: number | null | undefined,
): string | undefined {
    if (!url) return undefined;
    if (pageStart == null) return url;
    return `${url}#page=${pageStart + 1}`;
}
